import {
  AssessmentStatus,
  type TranscriptEntry as GqlTranscriptEntry,
} from '../../_generated/graphql'
import {
  CustomLabelValueMap,
  resolveSpeakerLabel,
} from '../services/custom-label/custom-label.service'

type TranscriptEntry = Pick<
  GqlTranscriptEntry,
  'speaker' | 'speakedAt' | 'quote'
>

export interface FormatTranscriptAsTextOptions {
  /** 指定時は話者名を表示用カスタムラベルに解決する。 */
  speakerLabelValues?: CustomLabelValueMap
}

/**
 * 文字起こしテキスト用ファイル名を組み立てる。
 * `{商談名}_{ユーザー名}_transcript_{YYYYMMDD}.txt` 形式。
 * パス区切り等のファイル名に使えない文字は除去し、空白は `_` に置換する。
 *
 * 商談名・ユーザー名が禁止文字のみで構成されている場合、sanitize の結果が
 * 空文字になり `_<user>_transcript_*.txt` 等の判別不能なファイル名が生成
 * されるため、それぞれ `untitled` / `unknown` でフォールバックする。
 */
export function buildTranscriptFilename(
  meetingName: string,
  userName: string,
  date: Date,
): string {
  const meeting = sanitizeFilenamePart(meetingName) || 'untitled'
  const user = sanitizeFilenamePart(userName) || 'unknown'
  return `${meeting}_${user}_transcript_${formatDateYYYYMMDD(date)}.txt`
}

/**
 * 文字起こし配列を `[話者] HH:MM:SS\n本文` 形式の整形済みテキストへ変換する。
 *
 * Storage 上の原本を直接 DL する案 A は、ロールプレイ二者通話のように
 * `filePath` の中身が JSON のまま残るデータ経路があり、`.txt` を期待する
 * 取り込み先(ASC 等)で破綻するため、GraphQL から取得済みの transcript
 * 配列をクライアント側で整形する案 B に戻している(SBL-527)。
 *
 * - `quote` が空白のみのエントリは除外する(空段落の生成を避けるため)
 * - `speakerLabelValues` 指定時は話者名を表示用カスタムラベルに解決する
 * - 段落間は空行で区切り、末尾は LF で閉じる(エディタでの可読性確保)
 */
export function formatTranscriptAsText(
  transcript: ReadonlyArray<TranscriptEntry>,
  options: FormatTranscriptAsTextOptions = {},
): string {
  const labelValues = options.speakerLabelValues ?? {}
  const paragraphs = transcript
    .filter((entry) => entry.quote.trim().length > 0)
    .map((entry) => {
      const speaker = resolveSpeakerLabel(entry.speaker, labelValues)
      return `[${speaker}] ${entry.speakedAt}\n${entry.quote}`
    })
  if (paragraphs.length === 0) return ''
  return paragraphs.join('\n\n') + '\n'
}

/**
 * 文字起こしテキストをダウンロード可能か判定する。
 * `status === COMPLETED` かつ非空白 `quote` を持つエントリが 1 件以上のときに true。
 *
 * 案 B(クライアント側整形)では `filePath` を参照しないため、`'NOT SET'`
 * かどうかを問わず transcript 配列のみで判定する。
 *
 * `quote` の空白判定が `formatTranscriptAsText` のフィルタと揃っていないと、
 * 全 entry が空白のみのケースでボタンは有効なのに押下しても無音で何も
 * 起きない状態になるため、ここで存在チェックも兼ねる。
 */
export function canDownloadTranscript(
  assessment:
    | {
        status: AssessmentStatus
        businessMeeting: {
          transcript?: ReadonlyArray<Pick<GqlTranscriptEntry, 'quote'>>
        }
      }
    | null
    | undefined,
): boolean {
  if (assessment?.status !== AssessmentStatus.Completed) return false
  const transcript = assessment.businessMeeting.transcript ?? []
  return transcript.some((entry) => entry.quote.trim().length > 0)
}

function formatDateYYYYMMDD(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}${m}${d}`
}

/**
 * ファイル名で禁止される文字 (`<>:"/\?*`) を除去し、連続する空白を `_` に置換する。
 */
function sanitizeFilenamePart(value: string): string {
  return value.replace(/[<>:"/\\?*]/g, '').replace(/\s+/g, '_')
}
